import { Controller, type Control } from "react-hook-form";
import MenuDropdown from "@/components/MenuDropdown";
import type { CreateTestPersonFormValues, DropdownOption } from "./types";

type MembershipSectionProps = {
  control: Control<CreateTestPersonFormValues>;
  classificationOptions: DropdownOption[];
  statusOptions: DropdownOption[];
  sectionOptions: DropdownOption[];
  noneOption: DropdownOption;
  disabled?: boolean;
  onStatusSelect?: (status: string) => void;
};

export default function MembershipSection({
  control,
  classificationOptions,
  statusOptions,
  sectionOptions,
  noneOption,
  disabled,
  onStatusSelect
}: MembershipSectionProps) {
  return (
    <div data-testid="create-test-person-membership-section">
      <div className="text-title-medium mb-4">Membership</div>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            control={control}
            name="classificationId"
            render={({ field }) => (
              <MenuDropdown
                label="Classification"
                options={classificationOptions}
                value={classificationOptions.find((option) => option.value === field.value) ?? noneOption}
                onSelect={(option) => field.onChange(option.value)}
                getLabel={(option) => option.label}
                renderItem={(option) => option.label}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <MenuDropdown
                label="Status"
                options={statusOptions}
                value={statusOptions.find((option) => option.value === field.value) ?? noneOption}
                onSelect={(option) => {
                  field.onChange(option.value);
                  onStatusSelect?.(option.value);
                }}
                getLabel={(option) => option.label}
                renderItem={(option) => option.label}
                disabled={disabled}
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="sectionId"
          rules={{
            validate: (value, formValues) =>
              !formValues.programType || Boolean(value) || "Section is required when program is set"
          }}
          render={({ field, fieldState }) => (
            <div>
              <MenuDropdown
                label="Section"
                options={sectionOptions}
                value={sectionOptions.find((option) => option.value === field.value) ?? noneOption}
                onSelect={(option) => field.onChange(option.value)}
                getLabel={(option) => option.label}
                renderItem={(option) => option.label}
                disabled={disabled}
              />
              {fieldState.error?.message && (
                <p className="mt-1 text-sys-brand-error text-body-small">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
}
